import React, { useState, useEffect } from 'react';
import { useSession } from '../../hooks/useSession';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, 
  Phone, 
  ShoppingBag, 
  LogOut,
  Edit2,
  Settings,
  Award,
  Package,
  ChevronRight,
  Shield,
  TrendingUp
} from 'lucide-react';
import toast from 'react-hot-toast';
import './Profile.css';

const Profile = () => {
  const { user, clearSession } = useSession();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');

  useEffect(() => {
    if (user?.name) {
      setEditedName(user.name);
    }
  }, [user]);

  const handleLogout = () => {
    toast.success('Logged out successfully');
    clearSession();
    navigate('/');
  };

  const handleEditProfile = () => {
    if (isEditing) {
      // Save changes
      toast.success('Profile updated successfully!');
      setIsEditing(false);
    } else {
      setIsEditing(true);
    }
  };

  if (!user) {
    return (
      <motion.div 
        className="profile-page"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="container-custom">
          <motion.div 
            className="profile-card login-required-card"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            <motion.div
              animate={{ 
                scale: [1, 1.1, 1],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <span className="login-required-icon">🔒</span>
            </motion.div>
            <h2>Please Login</h2>
            <p>Login to view your profile and manage your bookings</p>
            <motion.button 
              onClick={() => navigate('/login')} 
              className="btn-primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Login Now
              <ChevronRight size={18} />
            </motion.button>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  const stats = [
    { icon: ShoppingBag, label: 'Total Orders', value: '0' },
    { icon: Award, label: 'Member Since', value: 'Jan 2024' },
    { icon: TrendingUp, label: 'Loyalty Points', value: '250' },
  ];

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 300, damping: 30 },
    },
  };

  return (
    <motion.div 
      className="profile-page"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4 }}
    >
      <div className="container-custom">
        <motion.div 
          className="profile-card"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Decorative Elements */}
          <div className="profile-decoration">
            <motion.div 
              className="decoration-circle"
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 180, 360],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear",
              }}
            />
          </div>

          {/* Profile Header */}
          <motion.div 
            className="profile-header"
            variants={itemVariants}
          >
            <motion.div 
              className="profile-avatar-wrapper"
              whileHover={{ scale: 1.05 }}
            >
              <div className="profile-avatar">
                {user.name?.charAt(0) || 'U'}
              </div>
              <motion.button
                className="avatar-edit-btn"
                onClick={() => setIsEditing(!isEditing)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Edit2 size={14} />
              </motion.button>
            </motion.div>

            <AnimatePresence mode="wait">
              {isEditing ? (
                <motion.div
                  key="edit"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="profile-name-edit"
                >
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="name-input"
                    placeholder="Enter your name"
                  />
                </motion.div>
              ) : (
                <motion.h1 
                  key="name"
                  className="profile-name"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {user.name}
                </motion.h1>
              )}
            </AnimatePresence>
            
            <p className="profile-email">
              <Mail size={14} className="inline-icon" />
              {user.email}
            </p>
            
            {user.phone && (
              <p className="profile-phone">
                <Phone size={14} className="inline-icon" />
                {user.phone}
              </p>
            )}
          </motion.div>

          {/* Stats Grid */}
          <motion.div 
            className="profile-stats"
            variants={itemVariants}
          >
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div 
                  key={index}
                  className="stat-item"
                  whileHover={{ y: -4 }}
                >
                  <Icon size={20} className="stat-icon" />
                  <span className="stat-value">{stat.value}</span>
                  <span className="stat-label">{stat.label}</span>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Profile Actions */}
          <motion.div 
            className="profile-actions"
            variants={itemVariants}
          >
            <motion.button 
              className="btn-primary edit-btn"
              onClick={handleEditProfile}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Edit2 size={18} />
              {isEditing ? 'Save Changes' : 'Edit Profile'}
            </motion.button>
            
            <motion.button 
              className="btn-settings"
              onClick={() => navigate('/settings')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Settings size={18} />
              Settings
            </motion.button>

            <motion.button 
              className="btn-logout-profile"
              onClick={handleLogout}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <LogOut size={18} />
              Logout
            </motion.button>
          </motion.div>
        </motion.div>

        {/* Bookings Section */}
        <motion.div 
          className="bookings-section"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div 
            className="bookings-header"
            variants={itemVariants}
          >
            <h2 className="section-title">
              <Package className="section-icon" />
              Your Bookings
            </h2>
            <span className="booking-count">0 bookings</span>
          </motion.div>

          <motion.div 
            className="bookings-empty"
            variants={itemVariants}
          >
            <motion.div
              animate={{ 
                scale: [1, 1.05, 1],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <span className="empty-icon">📋</span>
            </motion.div>
            <h3>No Bookings Yet</h3>
            <p>Start your bullion trading journey today with Tatvam Silverr!</p>
            <motion.button 
              onClick={() => navigate('/products')} 
              className="btn-outline"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Browse Products
              <ChevronRight size={18} />
            </motion.button>
          </motion.div>
        </motion.div>

        {/* Security Badge */}
        <motion.div 
          className="profile-security"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <Shield size={16} className="security-icon" />
          <span>Your account is secured with 256-bit encryption</span>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Profile;